'use client'

import { Button } from '@comp/material'
import useFetch from '@hook/useFetch'
import { commentTypes } from '@lib/config'
import { get, map, range, take } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBoolean, useList } from 'react-use'
import { Waypoint } from 'react-waypoint'
import CommentInput from './input'
import CommentList from './list'
import CommentRow from './list/row'

const pageSize = 10
export default function Comment({
  newsId,
  topicId,
  commentType,
  className
}) {
  const ts = useTranslations('article')

  // 是否显示评论
  const [showComment, setShowComment] = useBoolean(false)
  // 获取评论列表
  const [pageIndex, setPageIndex] = useState(1)
  // 加载中
  const [loading, toggleLoading] = useBoolean(false)
  // 总数
  const [totals, setTotals] = useState(0)
  // 获取更多评论
  const handleMore = useCallback(() => {
    setPageIndex((d) => d + 1)
  }, [setPageIndex])

  // 最新添加的评论列表
  const [newComment, { push: addNewComment }] = useList([])

  return (
    <Waypoint onEnter={() => setShowComment(true)}>
      <div className={className}>
        {
          commentType !== commentTypes.close && (
            <CommentInput
              newsId={newsId}
              topicId={topicId}
              commentType={commentType}
              onAdd={addNewComment}
            />
          )
        }
        {
          showComment && (
            <>
              <HotComment newsId={newsId} topicId={topicId} commentType={commentType} />
              <div className="mt-pix-50">
                {
                  totals > 0 && (
                    <div className="text-f.9">{ts('allComment')}</div>
                  )
                }
                <div className="mt-pix-20">
                  {
                    map(newComment, (item) => (
                      <CommentRow
                        data={item}
                        newsId={newsId}
                        topicId={topicId}
                        commentType={commentType}
                        key={item.id}
                      />
                    ))
                  }
                  {
                    map(range(pageIndex), (idx) => (
                      <CommentPage
                        key={idx}
                        pageIndex={idx + 1}
                        newsId={newsId}
                        topicId={topicId}
                        commentType={commentType}
                        toggleLoading={toggleLoading}
                        changeTotals={setTotals}
                      />
                    ))
                  }
                </div>
                {
                  (totals > pageSize * pageIndex || loading) && (
                    <div className="mt-pix-30 text-center">
                      <Button
                        variant="outlined"
                        className="rounded-2xl"
                        onClick={handleMore}
                        disabled={loading}
                      >
                        {loading ? ts('loading') : ts('viewMore')}
                      </Button>
                    </div>
                  )
                }
              </div>
            </>
          )
        }
      </div>
    </Waypoint>
  )
}

function HotComment({
  newsId,
  topicId,
  commentType
}) {
  const ts = useTranslations('article')
  const [fetchUrl, fetchParams] = useMemo(() => [
    newsId ? 'news/comments' : 'topic/comments',
    newsId
      ? { newsId, offset: 0, limit: 10 }
      : { topicId, offset: 0, limit: 10 }
  ], [newsId, topicId])
  const { data } = useFetch(fetchUrl, fetchParams)

  return (
    <CommentList
      title={ts('hotComment')}
      data={take(data, 2)}
      newsId={newsId}
      commentType={commentType}
      className="mt-pix-20"
    />
  )
}

function CommentPage({
  pageIndex,
  newsId,
  topicId,
  commentType,
  toggleLoading,
  changeTotals
}) {
  const [fetchUrl, fetchParams] = useMemo(() => [
    newsId ? 'news/comments' : 'topic/comments',
    newsId
      ? { newsId, offset: (pageIndex - 1) * pageSize, limit: pageSize }
      : { topicId, offset: (pageIndex - 1) * pageSize, limit: pageSize }
  ], [newsId, topicId, pageIndex])

  const { data, isLoading } = useFetch(
    fetchUrl,
    fetchParams
  )
  useEffect(() => {
    changeTotals(get(data, 'headers.x-total-count', 0))
  }, [data, changeTotals])

  useEffect(() => {
    toggleLoading(isLoading)
  }, [toggleLoading, isLoading])

  if (isLoading) return null

  return (
    map(data, (item) => (
      <CommentRow
        data={item}
        newsId={newsId}
        topicId={topicId}
        commentType={commentType}
        key={item.id}
      />
    ))
  )
}
